import React from "react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Eyebrow, Resources } from "@/components/ServiceSections";
import { TrustCommitments } from "@/components/TechSections";
import { Info, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES, SCENARIOS, TRUST_COMMITMENTS } from "@/content/services";

export default function CaseStudies() {
  return (
    <div data-testid="case-studies-page">
      <PageHero
        breadcrumb={["Home", "Engagement Scenarios"]}
        pill="How Engagements Run"
        titleLines={[[{ t: "What working with us " }, { t: "looks like.", accent: true }]]}
        description="Four representative engagement scenarios showing how a Lanvaus engagement is scoped, delivered and evidenced — the situation, the work, and the artifacts you end up holding."
        primaryCta={{ label: "Discuss Your Situation", to: "/contact" }}
        secondaryCta={{ label: "View All Services", to: "/services" }}
        image={IMAGES.socCenter}
        showCompliance={false}
      />

      <section className="mx-auto max-w-7xl px-5 pt-14 sm:pt-20 sm:px-8 lg:px-16" data-testid="scenarios-disclaimer">
        <Reveal>
          <div className="flex items-start gap-4 rounded-xl border border-[#8E9AEA]/35 bg-[#8E9AEA]/[0.07] p-6 sm:p-7">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#8E9AEA]" />
            <div>
              <p className="font-display text-base font-bold text-white">
                These are illustrative scenarios, not named client case studies.
              </p>
              <p className="mt-2 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                We do not publish client names, logos or invented metrics. Each scenario below describes a realistic
                engagement shape — the situation a team was in, the work in scope, and the artifacts delivered — so you
                can judge fit before a call. Ask us on a call and we will walk you through the actual deliverable
                formats, redacted.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 sm:py-20 sm:px-8 lg:px-16" data-testid="scenarios-list">
        <Reveal>
          <Eyebrow>Representative Engagement Scenarios</Eyebrow>
          <h2 className="mt-5 max-w-3xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.6rem] lg:leading-[1.14]">
            Four situations we are <span className="text-[#8E9AEA]">brought into most often.</span>
          </h2>
        </Reveal>
        <div className="mt-10 sm:mt-14 grid gap-5 lg:grid-cols-2">
          {SCENARIOS.map((s, i) => (
            <Reveal key={s.sector} delay={(i % 2) * 0.08}>
              <article
                className="glass-card flex h-full flex-col p-6 sm:p-8"
                data-testid={`scenario-${s.sector.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[#8E9AEA]/40 bg-[#8E9AEA]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
                    {s.sector}
                  </span>
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A6B0C9]">
                    {s.size}
                  </span>
                  <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                    Illustrative
                  </span>
                </div>

                <h3 className="mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-[#B7BFDD]">The situation</h3>
                <p className="mt-2.5 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{s.situation}</p>

                <h3 className="mt-7 text-[11px] font-bold uppercase tracking-[0.2em] text-[#B7BFDD]">In scope</h3>
                <ul className="mt-3 flex flex-col gap-2.5">
                  {s.work.map((w) => (
                    <li key={w} className="flex items-start gap-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                      {w}
                    </li>
                  ))}
                </ul>

                <h3 className="mt-7 text-[11px] font-bold uppercase tracking-[0.2em] text-[#B7BFDD]">What they walked away with</h3>
                <p className="mt-2.5 flex-1 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{s.outcome}</p>

                <Link to="/contact" className="btn-secondary mt-8 self-start" data-testid={`scenario-cta-${i}`}>
                  Discuss a similar engagement
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <TrustCommitments
        items={TRUST_COMMITMENTS}
        title={{ pre: "Before you sign anything, ", accent: "here's how we work." }}
      />

      <Resources resources={[]} pagePath="/case-studies" />

      <CTABand
        title={{ pre: "Tell us where you ", accent: "actually stand." }}
        copy="A 30-minute call with an engineer, not a salesperson. We'll tell you what we would do first, and what we would not bother with."
        primary={{ label: "Get a Consultation", to: "/contact" }}
        secondary={{ label: "View All Services", to: "/services" }}
      />
    </div>
  );
}
